/**
 * UrgentCoverage — 2026-06-13
 *
 * Priority "coverage / help" alert for the sales floor. A normal team
 * message is too quiet — this reaches EVERY crew member at the store
 * (on shift or not), lands as a heavy lock-screen push (see
 * service-worker.js `urgent_alert`), and pops a full-width red banner
 * with a chime + vibration in-app no matter what screen you're on.
 *
 * Exports:
 *   - <UrgentAlertBanner user />   → mount once at the app root. Listens
 *     for realtime `urgent_alert` events and shows the pop-over banner
 *     with one-tap "I can take it" / "Can't" replies.
 *   - <UrgentCoverageModal user open onClose />  → the compose sheet.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HandPalm, X as XIcon, Siren, Lifebuoy, Users, CheckCircle } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Short two-tone attention chime via Web Audio — no asset to load.
// Respects the app-wide mute flag other themes use (`wow:audio:muted`).
function playAlertChime() {
  try {
    if (localStorage.getItem("wow:audio:muted") === "1") return;
  } catch { /* ignore */ }
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const tones = [[880, 0], [660, 0.18], [880, 0.36]];
    tones.forEach(([freq, at]) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "square";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.0001, now + at);
      gain.gain.exponentialRampToValueAtTime(0.22, now + at + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + at + 0.16);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + at);
      osc.stop(now + at + 0.17);
    });
    setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 900);
  } catch { /* audio blocked until a user gesture — fail silently */ }
}

const KIND_META = {
  coverage: { label: "Coverage needed", Icon: Users, hint: "e.g. Mario called off — need a closer 4:30–9:30" },
  help: { label: "Help on the floor", Icon: Lifebuoy, hint: "e.g. Slammed at the registers, need backup up front" },
};

export function UrgentCoverageModal({ user, open, onClose }) {
  const [kind, setKind] = useState("coverage");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (open) { setKind("coverage"); setNote(""); }
  }, [open]);

  if (!open) return null;

  const send = async () => {
    if (!user?.crew_id) return;
    setSending(true);
    try {
      await axios.post(`${API}/messages/urgent`, {
        from_id: user.crew_id,
        from_name: user.first_name || "A teammate",
        alert_kind: kind,
        note: note.trim(),
      });
      toast.success("Alert sent to the whole crew ⚡");
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the alert");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[9998] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="urgent-coverage-modal"
    >
      <div
        className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <div className="px-5 pt-5 pb-3 bg-rose-600 text-white flex items-center gap-2">
          <Siren weight="fill" className="w-5 h-5" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black uppercase tracking-widest text-rose-100">Priority alert</p>
            <p className="text-lg font-black leading-tight">Rally the crew</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/20" data-testid="urgent-modal-close" aria-label="Close">
            <XIcon weight="bold" className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {Object.entries(KIND_META).map(([key, m]) => {
              const active = kind === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setKind(key)}
                  className={`rounded-2xl border-2 px-3 py-3 flex flex-col items-center gap-1.5 transition-all ${active ? "border-rose-500 bg-rose-50 ring-2 ring-rose-200" : "border-slate-200 hover:border-slate-300"}`}
                  data-testid={`urgent-kind-${key}`}
                >
                  <m.Icon weight={active ? "fill" : "regular"} className={`w-6 h-6 ${active ? "text-rose-600" : "text-slate-500"}`} />
                  <span className={`text-[13px] font-black ${active ? "text-rose-700" : "text-slate-600"}`}>{m.label}</span>
                </button>
              );
            })}
          </div>

          <div>
            <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 block mb-1">
              What&apos;s going on?
            </label>
            <textarea
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={KIND_META[kind].hint}
              className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-rose-400 focus:ring-2 focus:ring-rose-100 resize-none"
              data-testid="urgent-note"
              autoFocus
            />
          </div>

          <div className="flex items-center gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2">
            <Users weight="bold" className="w-4 h-4 text-amber-700 flex-shrink-0" />
            <p className="text-[11px] text-amber-800 font-medium">
              Pings <span className="font-black">everyone at your store</span> — on shift or not — with a lock-screen alert.
            </p>
          </div>

          <button
            type="button"
            onClick={send}
            disabled={sending}
            className="w-full rounded-2xl bg-rose-600 hover:bg-rose-700 text-white font-black text-sm py-3.5 flex items-center justify-center gap-2 disabled:opacity-50 shadow-lg shadow-rose-600/30"
            data-testid="urgent-send"
          >
            <Siren weight="fill" className="w-5 h-5" />
            {sending ? "Sending…" : "Send priority alert"}
          </button>
        </div>
      </div>
    </div>
  );
}

export function UrgentAlertBanner({ user }) {
  // The currently-shown alert (only one at a time; a newer one replaces it).
  const [alert, setAlert] = useState(null); // {message_id, alert_kind, from_name, body}
  const [acked, setAcked] = useState("");    // "can_take" | "cant" | ""
  const [takerCount, setTakerCount] = useState(0);
  const dismissTimer = useRef(null);

  const clearAll = useCallback(() => {
    if (dismissTimer.current) { clearTimeout(dismissTimer.current); dismissTimer.current = null; }
  }, []);

  useEffect(() => {
    const onNotif = (e) => {
      const evt = e?.detail || {};
      if (evt.kind === "urgent_alert") {
        const d = evt.data || {};
        // Don't pop the banner on your own alert.
        if (d.from_id && user?.crew_id && d.from_id === user.crew_id) return;
        clearAll();
        setAlert({
          message_id: d.message_id || "",
          alert_kind: d.alert_kind || "coverage",
          from_name: d.from_name || "A teammate",
          body: evt.body || d.body || "",
        });
        setAcked("");
        setTakerCount(0);
        playAlertChime();
        try { if (navigator.vibrate) navigator.vibrate([400, 120, 400, 120, 400]); } catch { /* ignore */ }
      } else if (evt.kind === "urgent_ack_update") {
        // Live responder count for the open banner.
        setAlert((cur) => {
          if (cur && cur.message_id === evt.message_id) {
            const takers = (evt.acks || []).filter((a) => a.response === "can_take");
            setTakerCount(takers.length);
          }
          return cur;
        });
      }
    };
    window.addEventListener("wow:notification", onNotif);
    return () => { window.removeEventListener("wow:notification", onNotif); clearAll(); };
  }, [user?.crew_id, clearAll]);

  const respond = async (response) => {
    if (!alert?.message_id || !user?.crew_id) return;
    setAcked(response);
    try {
      const r = await axios.post(`${API}/messages/${alert.message_id}/ack`, {
        crew_id: user.crew_id,
        crew_name: user.first_name || "",
        response,
      });
      const takers = (r.data?.acks || []).filter((a) => a.response === "can_take");
      setTakerCount(takers.length);
      toast.success(response === "can_take" ? "Nice — they know you've got it ✋" : "Marked — thanks for the heads up");
    } catch (e) {
      setAcked("");
      toast.error(e?.response?.data?.detail || "Couldn't send your reply");
      return;
    }
    // Auto-dismiss shortly after responding.
    clearAll();
    dismissTimer.current = setTimeout(() => setAlert(null), 2600);
  };

  if (!alert) return null;
  const label = alert.alert_kind === "help" ? "HELP ON THE FLOOR" : "COVERAGE NEEDED";

  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 z-[9999] w-[calc(100%-16px)] max-w-md"
      style={{ top: "calc(env(safe-area-inset-top, 0px) + 8px)" }}
      data-testid="urgent-alert-banner"
      role="alert"
    >
      <div className="rounded-2xl bg-rose-600 text-white shadow-2xl shadow-rose-900/40 overflow-hidden animate-[pulse_1.6s_ease-in-out_infinite]">
        <div className="px-4 pt-3 pb-2 flex items-start gap-2.5">
          <Siren weight="fill" className="w-6 h-6 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-black uppercase tracking-widest text-rose-100">{label}</p>
            <p className="text-sm font-black leading-tight">{alert.from_name}</p>
            {alert.body && <p className="text-[13px] text-rose-50 mt-0.5 break-words">{alert.body}</p>}
          </div>
          <button
            onClick={() => { clearAll(); setAlert(null); }}
            className="p-1 rounded-full hover:bg-white/20 flex-shrink-0"
            data-testid="urgent-banner-dismiss"
            aria-label="Dismiss"
          >
            <XIcon weight="bold" className="w-4 h-4" />
          </button>
        </div>
        {acked ? (
          <div className="px-4 pb-3 flex items-center gap-1.5 text-rose-50" data-testid="urgent-banner-acked">
            <CheckCircle weight="fill" className="w-4 h-4" />
            <span className="text-[13px] font-bold">
              {acked === "can_take" ? "You're on it" : "Marked as can't"}
              {takerCount > 1 ? ` · ${takerCount} covering` : ""}
            </span>
          </div>
        ) : (
          <div className="px-3 pb-3 grid grid-cols-2 gap-2">
            <button
              onClick={() => respond("can_take")}
              className="rounded-xl bg-white text-rose-700 font-black text-sm py-2.5 flex items-center justify-center gap-1.5 active:scale-95 transition-transform"
              data-testid="urgent-banner-take"
            >
              <HandPalm weight="fill" className="w-4 h-4" /> I can take it
            </button>
            <button
              onClick={() => respond("cant")}
              className="rounded-xl bg-rose-800/60 text-white font-bold text-sm py-2.5 active:scale-95 transition-transform"
              data-testid="urgent-banner-cant"
            >
              Can&apos;t
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
